// @ts-check
import { fingerprintErrorReport, normalizeErrorReport } from './error-report.js';

/**
 * Browser error reporter shared by the game client and the admin dashboard.
 *
 * Every browser dependency is injected, so this module stays DOM-free and both
 * bundles use one implementation instead of maintaining a copy each.
 */

/** Sends allowed per page load, so a render loop that throws cannot flood the API. */
export const DEFAULT_MAX_REPORTS = 25;

/** How long an identical error stays suppressed after being sent. */
export const DEFAULT_DEDUPE_WINDOW_MS = 60_000;

/**
 * @typedef {Object} ErrorReporterOptions
 * @property {string} endpoint - Absolute or same-origin URL of the ingest route.
 * @property {string} source - 'client' or 'admin'.
 * @property {(input: any, init: any) => Promise<any>} [fetchImpl] - Transport, defaults to global fetch.
 * @property {() => Record<string, any>} [getContext] - Supplies userId / roomId / pageUrl / appVersion per report.
 * @property {() => number} [now] - Clock, injectable for tests.
 * @property {number} [maxReports] - Per-session send cap.
 * @property {number} [dedupeWindowMs] - Suppression window for identical errors.
 */

/**
 * Reduces an unknown thrown value to a message and stack pair.
 *
 * @param {any} value
 * @returns {{ message: string, stack: string }}
 */
function describeThrown(value) {
  if (value instanceof Error) {
    return { message: value.message || String(value), stack: value.stack || '' };
  }
  if (value && typeof value === 'object') {
    const message = value.message || value.reason || '';
    if (message) return { message: String(message), stack: String(value.stack || '') };
    try {
      return { message: JSON.stringify(value).slice(0, 500), stack: '' };
    } catch {
      return { message: 'Unserializable rejection value', stack: '' };
    }
  }
  return { message: String(value ?? 'Unknown error'), stack: '' };
}

/**
 * Creates an error reporter.
 *
 * @param {ErrorReporterOptions} options
 * @returns {{
 *   report: (input: any) => Promise<boolean>,
 *   handleErrorEvent: (event: any) => Promise<boolean>,
 *   handleRejectionEvent: (event: any) => Promise<boolean>,
 *   install: (target: any) => void,
 *   dispose: () => void,
 *   sentCount: () => number
 * }}
 */
export function createErrorReporter(options) {
  const {
    endpoint,
    source,
    fetchImpl,
    getContext = () => ({}),
    now = () => Date.now(),
    maxReports = DEFAULT_MAX_REPORTS,
    dedupeWindowMs = DEFAULT_DEDUPE_WINDOW_MS
  } = options || {};

  /** @type {Map<string, number>} */
  const lastSentAt = new Map();
  let sent = 0;
  /** @type {any} */
  let installedTarget = null;

  /**
   * Submits one report. Resolves false when suppressed, capped, or undeliverable;
   * it must never throw, or the reporter becomes a source of errors itself.
   *
   * @param {any} input - { message, stack, kind } plus optional context overrides.
   * @returns {Promise<boolean>} Whether the report was handed to the transport.
   */
  async function report(input) {
    try {
      if (sent >= maxReports) return false;

      let context = {};
      try {
        context = getContext() || {};
      } catch {
        context = {};
      }

      const normalized = normalizeErrorReport({ source, ...context, ...input });
      if (!normalized) return false;

      const fingerprint = fingerprintErrorReport(normalized);
      const timestamp = now();
      const previous = lastSentAt.get(fingerprint);
      if (previous !== undefined && timestamp - previous < dedupeWindowMs) {
        return false;
      }
      lastSentAt.set(fingerprint, timestamp);
      sent += 1;

      const transport = fetchImpl || (typeof fetch === 'function' ? fetch : null);
      if (!transport) return false;

      await transport(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(normalized),
        // Lets a report survive the navigation that an error often triggers.
        keepalive: true
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * @param {any} event - An ErrorEvent-like object.
   * @returns {Promise<boolean>}
   */
  function handleErrorEvent(event) {
    const described = describeThrown(event?.error ?? event?.message);
    return report({
      kind: 'error',
      message: event?.message || described.message,
      stack: described.stack
    });
  }

  /**
   * @param {any} event - A PromiseRejectionEvent-like object.
   * @returns {Promise<boolean>}
   */
  function handleRejectionEvent(event) {
    const described = describeThrown(event?.reason);
    return report({
      kind: 'unhandledrejection',
      message: described.message,
      stack: described.stack
    });
  }

  /**
   * Subscribes to global error events on the given target (typically `window`).
   *
   * @param {any} target
   * @returns {void}
   */
  function install(target) {
    if (!target || typeof target.addEventListener !== 'function') return;
    installedTarget = target;
    target.addEventListener('error', handleErrorEvent);
    target.addEventListener('unhandledrejection', handleRejectionEvent);
  }

  /**
   * Removes previously installed listeners.
   *
   * @returns {void}
   */
  function dispose() {
    if (!installedTarget || typeof installedTarget.removeEventListener !== 'function') return;
    installedTarget.removeEventListener('error', handleErrorEvent);
    installedTarget.removeEventListener('unhandledrejection', handleRejectionEvent);
    installedTarget = null;
  }

  return { report, handleErrorEvent, handleRejectionEvent, install, dispose, sentCount: () => sent };
}

/**
 * Reads the signed-in user id from browser storage without throwing on a
 * storage-disabled or private-mode window.
 *
 * @param {any} storage
 * @returns {string}
 */
function storedUserId(storage) {
  try {
    const raw = storage?.getItem('puzzle_user');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.id) return String(parsed.id);
    }
    return String(storage?.getItem('discord_user_id') || '');
  } catch {
    return '';
  }
}

/**
 * Builds a reporter wired to a browser window and installs the global handlers.
 * Shared by the game client and the admin dashboard so the wiring exists once.
 *
 * Automated browsers are skipped: E2E and visual runs would otherwise fill the log
 * with deliberately triggered failures.
 *
 * @param {Object} options
 * @param {string} options.endpoint - Ingest URL.
 * @param {string} options.source - 'client' or 'admin'.
 * @param {any} options.win - The window object to observe.
 * @param {() => Record<string, any>} [options.extraContext] - Adds roomId / appVersion.
 * @returns {ReturnType<typeof createErrorReporter>|null} The reporter, or null when skipped.
 */
export function createBrowserErrorReporter({ endpoint, source, win, extraContext = () => ({}) }) {
  if (!win || typeof win.addEventListener !== 'function') return null;
  if (win.navigator?.webdriver) return null;

  const reporter = createErrorReporter({
    endpoint,
    source,
    fetchImpl: (url, init) => win.fetch(url, init),
    getContext: () => {
      let extra = {};
      try {
        extra = extraContext() || {};
      } catch {
        extra = {};
      }
      return {
        pageUrl: win.location?.pathname || '',
        userAgent: win.navigator?.userAgent || '',
        userId: storedUserId(win.localStorage),
        ...extra
      };
    }
  });

  reporter.install(win);
  return reporter;
}
